import { useEffect, useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';
import {
  autoCreateRouterForward,
  createRouterForward,
  type RouterAutoForwardFamily,
  type RouterAutoForwardResponse,
  type RouterForwardWritePayload,
  type RouterForwardWriteResponse,
  type RouterProbeLanHost,
} from '@/api/zoneRouter';
import { extractApiErrorLocalised } from '@/utils/apiError';
import { translatePortForwardError } from './portForwardErrors';

export const ROUTER_INTERFACE_OPTIONS = ['Wireguard0', 'GigabitEthernet1'] as const;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  zoneId: number | string;
  /** LAN hosts from the latest probe — populates the device picker.
   *  Empty array → picker degrades to a free-text MAC field. */
  lanHosts: RouterProbeLanHost[];
  /** Default interface — Wireguard0 when WG is configured, else the
   *  WAN interface. Operator can still override via dropdown. */
  defaultInterface?: string;
  /** Fires on successful create — parent refetches the probe so the
   *  forwards table picks up the new rule from the source of truth. */
  onCreated: () => void;
}

/**
 * Manual "Добавить проброс" dialog (2026-06-02 redesign).
 *
 * Changes from the legacy modal:
 *   - "IP/MAC устройства в LAN" free-text → device picker dropdown
 *     populated from `probe.lan_hosts`. Label is `hostname (ip)`,
 *     value is the MAC so the BE writes the MAC-binding path.
 *   - New "Назначение" segmented control: Camera RTSP / Camera API /
 *     Other. Auto-fills the internal_port (554 / 80 / —).
 *   - Auto-picks a free external_port via the Group A endpoint —
 *     submit hits forwards/auto first; on `endpoint_not_deployed` it
 *     transparently falls back to the legacy POST /forwards/ with the
 *     operator-supplied port.
 *
 * BE: POST /equipment_discovery/zones/<id>/router/forwards/auto/
 *     (legacy fallback: POST /equipment_discovery/zones/<id>/router/forwards/)
 */
export default function AddPortForwardDialog({
  open,
  onOpenChange,
  zoneId,
  lanHosts,
  defaultInterface,
  onCreated,
}: Props) {
  const { t, lang } = useT();
  const initialIface = defaultInterface || ROUTER_INTERFACE_OPTIONS[0];

  const [iface, setIface] = useState<string>(initialIface);
  const [protocol, setProtocol] = useState<'tcp' | 'udp'>('tcp');
  const [family, setFamily] = useState<RouterAutoForwardFamily>('rtsp');
  const [externalPort, setExternalPort] = useState<string>('');
  const [internalPort, setInternalPort] = useState<string>('554');
  /**
   * Tracks whether the operator typed in the internal_port input. While
   * false, switching the family auto-fills the suggested port (554 for
   * RTSP, 80 for API). Once true, the family switch leaves the field
   * alone + shows a "не совпадает с рекомендованным" warning so the
   * operator notices the mismatch without losing their typed value
   * (finding #15 from the 2026-06-02 test report).
   */
  const [userTouchedInternalPort, setUserTouchedInternalPort] = useState(false);
  /** Picker value — MAC. Empty string means "no device picked yet". */
  const [pickedMac, setPickedMac] = useState<string>('');
  /** Free-text fallback when probe has no hosts. */
  const [freeHost, setFreeHost] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** "BE not deployed" hint — non-fatal, surfaces above the error block. */
  const [fallbackNotice, setFallbackNotice] = useState<string | null>(null);

  // Pre-sort hosts by hostname/active state so the picker reads cleanly.
  const sortedHosts = useMemo(() => {
    const copy = [...lanHosts];
    copy.sort((a, b) => {
      if (a.active !== b.active) return a.active ? -1 : 1;
      const na = (a.host_name || a.ip || a.mac).toLowerCase();
      const nb = (b.host_name || b.ip || b.mac).toLowerCase();
      return na.localeCompare(nb);
    });
    return copy;
  }, [lanHosts]);

  useEffect(() => {
    if (!open) return;
    setIface(initialIface);
    setProtocol('tcp');
    setFamily('rtsp');
    setExternalPort('');
    setInternalPort('554');
    setUserTouchedInternalPort(false);
    setPickedMac('');
    setFreeHost('');
    setComment('');
    setError(null);
    setFallbackNotice(null);
  }, [open, initialIface]);

  // Family changes pre-fill the internal port — BUT only when the
  // operator hasn't typed in the internal_port field yet. Once they've
  // touched it we leave their value alone + surface a "не совпадает с
  // рекомендованным" warning below the field (finding #15).
  useEffect(() => {
    if (userTouchedInternalPort) return;
    if (family === 'rtsp') setInternalPort('554');
    else if (family === 'api') setInternalPort('80');
    // Other — keep whatever was there; "Другое" doesn't have a canonical
    // suggested port so there's nothing to auto-fill.
  }, [family, userTouchedInternalPort]);

  // Suggested port per family — drives the "не совпадает" warning AND
  // the auto-fill logic above. `null` means "no suggestion" (other).
  const suggestedInternalPort: number | null =
    family === 'rtsp' ? 554 : family === 'api' ? 80 : null;
  const internalPortMismatch =
    suggestedInternalPort != null &&
    internalPort !== '' &&
    Number(internalPort) !== suggestedInternalPort;

  const onHostChange = (val: string) => setPickedMac(val);

  /**
   * Resolve the to_host value the BE will receive. The MAC-binding
   * "Specific client from list" path is the preferred shape; we use it
   * whenever the operator picked a device from the dropdown. Free-text
   * mode is the legacy "Other client" surface (kept for the rare zone
   * with no probe data yet).
   */
  const resolvedHostInput = pickedMac
    ? sortedHosts.find(h => h.mac === pickedMac)
    : null;
  const toHostValue = pickedMac || freeHost.trim();

  const submit = async () => {
    if (submitting) return;
    setError(null);
    setFallbackNotice(null);

    const intNum = Number(internalPort);
    if (!Number.isInteger(intNum) || intNum < 1 || intNum > 65535) {
      setError(t('equipment.router.forwards.errors.bad_internal_port'));
      return;
    }
    if (!toHostValue) {
      setError(t('equipment.router.forwards.errors.missing_field'));
      return;
    }

    setSubmitting(true);
    try {
      // Auto-allocate path: MAC-bound + BE picks the external port. We
      // try this first whenever the operator picked a device from the
      // dropdown AND family is RTSP/API (i.e. the BE knows the family
      // base port).
      if (pickedMac && family !== 'other' && !externalPort) {
        const auto: RouterAutoForwardResponse = await autoCreateRouterForward(zoneId, {
          lan_host_mac: pickedMac,
          internal_port: intNum,
          family,
        });
        if (auto.ok) {
          onCreated();
          onOpenChange(false);
          return;
        }
        if (auto.error === 'endpoint_not_deployed') {
          // Fall through to the legacy POST with an operator-supplied
          // external port — surface a notice so they know they need to
          // type one in.
          setFallbackNotice(
            t('equipment.router.forwards.errors.endpoint_not_deployed'),
          );
          // Pre-fill a sensible default for the family.
          if (!externalPort) {
            setExternalPort(family === 'rtsp' ? '8554' : '8080');
          }
          setSubmitting(false);
          return;
        }
        if (auto.error === 'no_reachable_path') {
          setError(t('equipment.router.forwards.errors.no_reachable_path'));
          setSubmitting(false);
          return;
        }
        // Some other typed error — show its translation but don't
        // proceed; the operator can adjust + retry.
        setError(translatePortForwardError(t, auto.error, auto.detail));
        setSubmitting(false);
        return;
      }

      // Legacy / manual external-port path.
      const extNum = Number(externalPort);
      if (extNum === 80) {
        setError(t('equipment.router.forwards.errors.external_port_reserved'));
        setSubmitting(false);
        return;
      }
      if (!Number.isInteger(extNum) || extNum < 1 || extNum > 65535) {
        setError(t('equipment.router.forwards.errors.bad_external_port'));
        setSubmitting(false);
        return;
      }
      const payload: RouterForwardWritePayload = {
        interface: iface,
        protocol,
        external_port: extNum,
        internal_port: intNum,
        to_host: toHostValue,
        ...(comment.trim() ? { comment: comment.trim() } : {}),
      };
      const resp = await createRouterForward(zoneId, payload);
      const body = resp.data as RouterForwardWriteResponse;
      if (body.ok) {
        onCreated();
        onOpenChange(false);
      } else {
        setError(translatePortForwardError(t, body.error, body.detail));
      }
    } catch (err) {
      // Locale-aware error surface (finding #20).
      setError(
        extractApiErrorLocalised(
          isAxiosError(err) ? err : undefined,
          err instanceof Error && lang === 'ru' ? err.message : t('error.general'),
          lang,
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="relative max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('equipment.router.forwards.addDialog.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {/* Назначение — segmented control */}
          <div className="space-y-1.5">
            <Label>{t('equipment.router.forwards.field.family')}</Label>
            <FamilySegment value={family} onChange={setFamily} />
          </div>

          {/* Device picker (or free-text fallback) */}
          <div className="space-y-1.5">
            <Label htmlFor="pf-host">{t('equipment.router.forwards.field.toHost.picker')}</Label>
            {sortedHosts.length > 0 ? (
              <>
                <Select
                  id="pf-host"
                  value={pickedMac}
                  onChange={e => onHostChange(e.target.value)}
                  data-testid="pf-host-picker"
                >
                  <option value="">{t('equipment.router.forwards.field.toHost.pickerPlaceholder')}</option>
                  {sortedHosts.map(h => {
                    // Already-imported hint (finding #19) — the probe
                    // payload's `imported_as_camera_id` annotation lets
                    // us surface a "(уже импортирован)" suffix so the
                    // operator doesn't accidentally pick the same MAC
                    // twice. We DON'T fully `disabled` the option
                    // because operators occasionally add a SECOND
                    // forward (e.g. RTSP + API) to the same device —
                    // the suffix is the warning.
                    const imported = !!h.imported_as_camera_id;
                    return (
                      <option key={h.mac} value={h.mac}>
                        {(h.host_name || h.mac)}
                        {h.ip && h.ip !== '0.0.0.0' ? ` (${h.ip})` : ''}
                        {!h.active ? ` · ${t('equipment.router.probe.offline')}` : ''}
                        {imported ? ` · ${t('equipment.router.forwards.field.toHost.alreadyImported')}` : ''}
                      </option>
                    );
                  })}
                </Select>
                {resolvedHostInput && (
                  <p className="font-mono text-[10px] tabular-nums text-muted-foreground">
                    {resolvedHostInput.mac}
                    {resolvedHostInput.ip && resolvedHostInput.ip !== '0.0.0.0'
                      ? ` · ${resolvedHostInput.ip}`
                      : ''}
                    {resolvedHostInput.imported_as_camera_id && (
                      <span className="ml-1 font-sans text-[color:var(--color-warning)]">
                        · {t('equipment.router.forwards.field.toHost.alreadyImportedHint')}
                      </span>
                    )}
                  </p>
                )}
              </>
            ) : (
              <Input
                id="pf-host"
                value={freeHost}
                onChange={e => setFreeHost(e.target.value)}
                placeholder="aa:bb:cc:dd:ee:ff"
                className="font-mono"
                autoComplete="off"
              />
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="pf-iface">
                {t('equipment.router.forwards.field.interface')}
              </Label>
              <Select
                id="pf-iface"
                value={iface}
                onChange={e => setIface(e.target.value)}
              >
                {ROUTER_INTERFACE_OPTIONS.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pf-proto">
                {t('equipment.router.forwards.field.protocol')}
              </Label>
              <Select
                id="pf-proto"
                value={protocol}
                onChange={e => setProtocol(e.target.value as 'tcp' | 'udp')}
              >
                <option value="tcp">TCP</option>
                <option value="udp">UDP</option>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pf-ext">
                {t('equipment.router.forwards.field.externalPort')}
              </Label>
              <Input
                id="pf-ext"
                type="number"
                inputMode="numeric"
                value={externalPort}
                onChange={e => setExternalPort(e.target.value)}
                className="font-mono tabular-nums"
                placeholder={
                  family !== 'other' && pickedMac && !fallbackNotice
                    ? t('equipment.router.forwards.autoPort')
                    : family === 'rtsp'
                      ? '8554'
                      : family === 'api'
                        ? '8080'
                        : ''
                }
              />
              {family !== 'other' && pickedMac && !externalPort && !fallbackNotice && (
                <p className="text-[10px] italic text-muted-foreground">
                  {t('equipment.router.forwards.autoPortHint')}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pf-int">
                {t('equipment.router.forwards.field.internalPort')}
              </Label>
              <Input
                id="pf-int"
                type="number"
                inputMode="numeric"
                value={internalPort}
                onChange={e => {
                  setUserTouchedInternalPort(true);
                  setInternalPort(e.target.value);
                }}
                className="font-mono tabular-nums"
                placeholder="554"
                data-testid="pf-internal-port"
              />
              {internalPortMismatch && suggestedInternalPort != null && (
                <p
                  className="text-[10px] leading-snug text-[color:var(--color-warning)]"
                  data-testid="pf-internal-port-mismatch"
                >
                  {t('equipment.router.forwards.internalPortMismatch')
                    .replace('{{suggested}}', String(suggestedInternalPort))
                    .replace(
                      '{{family}}',
                      t(`equipment.router.forwards.field.family.${family}`),
                    )}
                </p>
              )}
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="pf-comment">
                {t('equipment.router.forwards.field.comment')}
              </Label>
              <Input
                id="pf-comment"
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder={t('equipment.router.forwards.field.commentPlaceholder')}
                maxLength={200}
              />
            </div>
          </div>

          {fallbackNotice && (
            <div className="rounded-lg bg-[color:var(--color-warning)]/10 px-3 py-2 text-xs text-[color:var(--color-warning)]">
              {fallbackNotice}
            </div>
          )}
          {error && (
            <div className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive" data-testid="pf-error">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={submitting}>
            {submitting && <Loader2 className="size-3.5 animate-spin" />}
            {t('equipment.router.forwards.addDialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Three-state segmented control for the "Назначение" field. Renders as
 * a horizontal row of equal-width buttons so the selection stays
 * visually flat — picking matches the polish-agent's chip aesthetic
 * (1px border, subtle bg-card baseline, primary-muted on select).
 *
 * Mobile breakpoint (finding #16): "Камера: RTSP" / "Камера: API" wrap
 * mid-segment at 390px. Solution — render two label tiers: a short
 * primary label always visible (RTSP / API / Другое / Other) + a
 * secondary "Камера:" prefix that shows only on sm+ breakpoints. This
 * keeps the segment compact AND preserves the semantic context on
 * desktop where the space is available.
 */
function FamilySegment({
  value,
  onChange,
}: {
  value: RouterAutoForwardFamily;
  onChange: (v: RouterAutoForwardFamily) => void;
}) {
  const { t } = useT();
  const cameraPrefix = t('equipment.router.forwards.field.family.cameraPrefix');
  const items: Array<{
    value: RouterAutoForwardFamily;
    short: string;
    showCameraPrefix: boolean;
  }> = [
    { value: 'rtsp', short: t('equipment.router.forwards.field.family.shortRtsp'), showCameraPrefix: true },
    { value: 'api', short: t('equipment.router.forwards.field.family.shortApi'), showCameraPrefix: true },
    { value: 'other', short: t('equipment.router.forwards.field.family.shortOther'), showCameraPrefix: false },
  ];
  return (
    <div
      className="flex w-full overflow-hidden rounded-md border border-border bg-card/40"
      role="radiogroup"
    >
      {items.map(item => {
        const selected = value === item.value;
        return (
          <button
            key={item.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(item.value)}
            className={cn(
              'flex-1 px-2 py-1.5 text-xs font-medium transition-colors',
              'first:rounded-l-md last:rounded-r-md',
              'min-w-0 whitespace-nowrap',
              selected
                ? 'bg-primary-muted text-primary-muted-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
            data-testid={`pf-family-${item.value}`}
          >
            {item.showCameraPrefix && (
              <span className="hidden sm:inline text-muted-foreground/80 sm:mr-1">
                {cameraPrefix}
              </span>
            )}
            <span className="tabular-nums">{item.short}</span>
          </button>
        );
      })}
    </div>
  );
}
